import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyCredit,
  getCreditApplications,
  getCreditBalances,
  getCustomerCreditById,
  getCustomerCredits,
  getEstimates,
  getInvoices,
  reverseCredit,
} from "@/features/credits/hooks/credit-client";
import type { ApplyCreditPayload } from "@/features/credits/types";
import { invalidateCreditApplicationQueries } from "@/features/finance/hooks/credit-cache";
import { creditKeys } from "@/features/credits/hooks/query-keys";
import { estimateKeys } from "@/features/estimates/hooks/query-keys";
import { invoiceKeys } from "@/features/invoices/hooks/query-keys";

export function useCustomerCredit(creditId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: creditId ? creditKeys.detail(creditId) : creditKeys.all,
    queryFn: () => getCustomerCreditById(creditId as string),
    enabled: Boolean(creditId) && (options?.enabled ?? true),
  });
}

export function useCreditBalances() {
  return useQuery({
    queryKey: creditKeys.balances(),
    queryFn: () => getCreditBalances(),
  });
}

export function useCustomerCredits(limit = 100, options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: creditKeys.list({ limit }),
    queryFn: () => getCustomerCredits({ limit }),
  });
}

export function useCreditApplications(limit = 100, options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: creditKeys.applicationList({ limit }),
    queryFn: () => getCreditApplications({ limit }),
  });
}

export function useInvoicesForCreditApplication(enabled: boolean, limit = 50) {
  return useQuery({
    enabled,
    queryKey: invoiceKeys.list({ limit }),
    queryFn: () => getInvoices({ limit }),
  });
}

export function useEstimatesForCreditApplication(enabled: boolean, limit = 50) {
  return useQuery({
    enabled,
    queryKey: estimateKeys.list({ limit }),
    queryFn: () => getEstimates({ limit }),
  });
}

export function useApplyCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ApplyCreditPayload) => applyCredit(payload),
    onSuccess: () => invalidateCreditApplicationQueries(queryClient),
  });
}

/** Reversal is the inverse of {@link useApplyCredit} and refreshes the same set. */
export function useReverseCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { application_id: string }) => reverseCredit(payload),
    onSuccess: () => invalidateCreditApplicationQueries(queryClient),
  });
}
